import * as React from 'react';
export * from 'original/Button/Button';
import Button, {ButtonProps} from 'original/Button/Button';
import * as _ from 'lodash';
import {withStyles, MuiThemeProvider, WithStyles, Theme, createStyles} from 'original/styles';
import {PropTypes} from '..';
import LinearProgress from 'original/LinearProgress';
import {Omit} from 'utility-types';
import Grow from 'original/Grow';
import update from 'immutability-helper';
import SvgIcon from '../SvgIcon/SvgIcon';
import nlp = require('compromise/builds/compromise.es6.js');
import Typography from '../Typography/Typography';
import * as classNames from 'classnames';
import {Link} from 'react-router';

const toGerund = _.memoize((child: React.ReactChild) => {
  if (_.isString(child)) {
    const conjugate = nlp(child)
      .verbs()
      .conjugate();
    return _.isEmpty(conjugate) ? child : `${conjugate[0].Gerund}...`;
  } else if (React.isValidElement(child)) {
    const childProps = child.props as any;
    return React.cloneElement(child, undefined, React.Children.map(childProps.children, child => toGerund(child)));
  }
  return child;
});

export function getRouterLinkProps(href: string) {
  let component;
  let to;
  if (href && href[0] === '/') {
    component = Link;
    to = href;
    return {component, to};
  }
  if (href) {
    return {href, target: '_blank'};
  }
  return {};
}

export interface ExtendedButtonProps extends Omit<ButtonProps, 'classes' | 'onClick' | 'color'> {
  component?: any;
  cancel?: boolean;
  onClick?(event: React.SyntheticEvent<any>): void | Promise<any>;
  color?: PropTypes.Color | 'error'; //TODO we should make it so that color prop type is changed to allow for the default types + every other main key we add to the main theme
  busyLabel?: string;
  caption?: React.ReactNode;
}

const initialState = {
  busy: false,
};

class ExtendedButton extends React.PureComponent<ExtendedButtonProps & WithStyles<typeof styles>, typeof initialState> {
  state = initialState;
  private unmounted: boolean;
  componentWillUnmount() {
    this.unmounted = true;
  }

  handleClick = async event => {
    const {onClick} = this.props;
    if (onClick) {
      // noinspection ES6MissingAwait
      const response = onClick(event);
      if (response) {
        try {
          this.setState({busy: true});
          await Promise.resolve(response);
        } finally {
          if (!this.unmounted) {
            this.setState({busy: false});
          }
        }
      }
    }
  };

  renderMuiButton = (buttonColor: PropTypes.Color) => {
    // noinspection JSUnusedLocalSymbols
    const {
      caption,
      disabled,
      onClick,
      cancel,
      busyLabel,
      color,
      children,
      className,
      href,
      classes: {cancel: cancelClass, progress, root, captionContainer, leftIcon, rightIcon, ...otherClasses},
      ...props
    } = this.props;
    const {busy} = this.state;
    const buttonClassName = classNames(cancel ? cancelClass : root, className);
    let updatedChildren: typeof children = busy
      ? React.Children.map(children, child => (busyLabel ? busyLabel : toGerund(child)))
      : children;
    updatedChildren = React.Children.map(updatedChildren, (child, index) => {
      if (_.get(child, 'type.muiName') === SvgIcon['muiName']) {
        return React.cloneElement(child as React.ReactElement, {className: index === 0 ? leftIcon : rightIcon});
      }
      return child;
    });
    return (
      <Button
        disabled={disabled || busy}
        onClick={this.handleClick}
        color={buttonColor}
        className={buttonClassName}
        classes={otherClasses}
        {..._.omit(props, 'handleSubmit')}
        {...getRouterLinkProps(href)}
      >
        {updatedChildren}
        <Grow in={busy} style={{transitionDelay: busy ? '1000ms' : undefined}}>
          <LinearProgress className={progress} />
        </Grow>
        {caption ? (
          <Typography className={captionContainer} variant="caption">
            {caption}
          </Typography>
        ) : (
          undefined
        )}
      </Button>
    );
  };

  render() {
    const {color} = this.props;
    const shouldOverridePrimary = color && !_.includes(['inherit', 'primary', 'secondary', 'default'], color);
    if (!shouldOverridePrimary) {
      return this.renderMuiButton(color as PropTypes.Color);
    }
    return (
      <MuiThemeProvider
        theme={(theme: Theme) => {
          return update(theme, {
            palette: {primary: {main: {$set: theme.palette[color].main}}},
          });
        }}
      >
        {this.renderMuiButton('primary')}
      </MuiThemeProvider>
    );
  }
}

const styles = theme =>
  createStyles({
    root: {
      margin: theme.spacing(0.5),
      flexShrink: 0,
    },
    cancel: {
      marginRight: 'auto',
    },
    progress: {
      transformOrigin: 'bottom center',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
    },
    captionContainer: {
      position: 'absolute',
      bottom: -20,
      whiteSpace: 'nowrap',
      left: 0,
      textTransform: 'initial',
    },
    sizeSmall: {},
    leftIcon: {},
    rightIcon: {},
    label: {
      // small size is used for the arrow in split button, if we end up using it anywhere else we'll have to find a different solution
      '$root:not($sizeSmall) & $leftIcon': {
        marginRight: theme.spacing(1),
      },
      '$root:not($sizeSmall) & $rightIcon': {
        marginLeft: theme.spacing(1),
      },
    },
  });

export default withStyles(styles)(ExtendedButton);
